import React, { useRef, useEffect } from 'react'
import { OverlayTrigger, Popover, Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter } from '@fortawesome/free-solid-svg-icons'
import FilterEditor from './FilterEditor'
import { useErrorContext } from 'contexts/ErrorContext'

function FilterableHeader({
  column,
  title,
  params,
  setParams,
  includeOrdering = true,
  sortColumn,
  options = [],
  getOptions,
  dashboardSetParams = null,
  popoverId = '',
  activePopoverId = null,
  setActivePopoverId = null,
  canEdit = true,
  style = {},
  referenceSearch = null,
  // The View this header belongs to, if any. Only the account-group popover reads it.
  dashboardId = null
}) {
  const { setErrorContext, clearErrorContext } = useErrorContext()

  useEffect(() => {
    const contextData = {}
    if (column?.database_column_name || column?.accessor_key) {
      contextData.column = column.database_column_name || column.accessor_key
    }
    if (column?.filter_type) {
      contextData.filterType = column.filter_type
    }
    if (Object.keys(contextData).length > 0) {
      setErrorContext(contextData)
    }
    return () => clearErrorContext()
  }, [column, setErrorContext, clearErrorContext])
  const showPopover = activePopoverId === popoverId
  const target = useRef(null)
  const childHandleApplyRef = useRef(null)

  // Scroll the column header into view when the popover opens
  useEffect(() => {
    if (showPopover && target.current) {
      target.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }
  }, [showPopover])

  const handleTogglePopover = (show, isFromHandleApply = false) => {
    if (setActivePopoverId) {
      // If closing the popover and we have a child handleApply function, call it
      // but only if it's not already being called from handleApply
      if (!show && !isFromHandleApply && childHandleApplyRef.current) {
        childHandleApplyRef.current()
      }
      setActivePopoverId(show ? popoverId : null)
    }
  }

  // Enhanced setParams that updates both table state and dashboard state
  const enhancedSetParams = (newParams) => {
    // Always update the table state for immediate UI response
    setParams(newParams)

    // Also update dashboard state if available for persistence
    if (dashboardSetParams) {
      dashboardSetParams(newParams)
    }
  }

  const renderFilterPopover = () => {
    const popoverOptions =
      options.length > 0
        ? options
        : getOptions
        ? getOptions(column.accessor_key)
        : []

    const popover = (
      <Popover
        id={`popover-${column.accessor_key}`}
        className="filterable-header-popover"
      >
        <Popover.Body>
          <div>
            {includeOrdering && sortColumn && (
              <>
                <div>
                  <div className="mb-2">
                    <strong className="small">Sort</strong>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      data-testid="sort-asc"
                      variant={
                        params.orderBy === sortColumn &&
                        params.orderDirection === 'asc'
                          ? 'primary'
                          : 'light'
                      }
                      size="sm"
                      disabled={!canEdit}
                      onClick={() => {
                        if (!canEdit) return
                        if (
                          params.orderBy === sortColumn &&
                          params.orderDirection === 'asc'
                        ) {
                          enhancedSetParams({
                            ...params,
                            orderBy: null,
                            orderDirection: null,
                            page: 1
                          })
                        } else {
                          enhancedSetParams({
                            ...params,
                            orderBy: sortColumn,
                            orderDirection: 'asc',
                            page: 1
                          })
                        }
                      }}
                    >
                      Sort Asc
                    </Button>
                    <Button
                      data-testid="sort-desc"
                      variant={
                        params.orderBy === sortColumn &&
                        params.orderDirection === 'desc'
                          ? 'primary'
                          : 'light'
                      }
                      size="sm"
                      disabled={!canEdit}
                      onClick={() => {
                        if (!canEdit) return
                        if (
                          params.orderBy === sortColumn &&
                          params.orderDirection === 'desc'
                        ) {
                          enhancedSetParams({
                            ...params,
                            orderBy: null,
                            orderDirection: null,
                            page: 1
                          })
                        } else {
                          enhancedSetParams({
                            ...params,
                            orderBy: sortColumn,
                            orderDirection: 'desc',
                            page: 1
                          })
                        }
                      }}
                    >
                      Sort Desc
                    </Button>
                  </div>
                </div>
              </>
            )}

            <hr className="my-2" />

            <FilterEditor
              column={column}
              params={params}
              setParams={enhancedSetParams}
              options={popoverOptions}
              dashboardId={dashboardId}
              referenceSearch={referenceSearch}
              onClose={() => handleTogglePopover(false, true)}
              onSetHandleApply={(handleApply) => {
                childHandleApplyRef.current = handleApply
              }}
              canEdit={canEdit}
            />
          </div>
        </Popover.Body>
      </Popover>
    )

    const isActive = hasActiveFilter() || (sortColumn && sortColumn !== '' && params.orderBy === sortColumn)

    return (
      <OverlayTrigger
        trigger="click"
        placement="bottom"
        overlay={popover}
        show={showPopover}
        onToggle={(nextShow) => {
          if (!nextShow) {
            handleTogglePopover(false, false)
          }
        }}
        rootClose
      >
        <Button
          ref={target}
          data-testid={`sort-toggle-${column.accessor_key}`}
          variant={isActive ? 'primary' : 'light'}
          size="sm"
          className="ms-1 border-0"
          style={{
            padding: '2px 6px',
            minWidth: 'auto'
          }}
          onClick={(e) => {
            e.stopPropagation()
            handleTogglePopover(!showPopover)
          }}
          title={!canEdit ? "View only - only the creator or permitted users can edit filters" : ""}
        >
          <FontAwesomeIcon icon={faFilter} size="xs" />
        </Button>
      </OverlayTrigger>
    )
  }

  const hasActiveFilter = () => {
    const searchKey = column.accessor_key
    const operatorKey = `${column.accessor_key}Operator`
    const search = params[searchKey]
    const operator = params[operatorKey]

    switch (column.filter_type) {
      case 'basic':
        // Filter is active if:
        // 1. There's a search value, OR
        // 2. The operator is not the default 'contains' (e.g., 'is_empty', 'is_not_empty')
        return !!(search || (operator && operator !== 'contains'))
      case 'boolean':
        return !!(search && search !== '')
      case 'min_max':
      case 'min_max_percent':
      case 'time_in_seconds': {
        const mmOperatorKey = `${column.accessor_key}Operator`
        const mmOperatorValueKey = `${column.accessor_key}OperatorValue`
        const mmOperator = params[mmOperatorKey]
        // Active if a non-range operator is set (equals requires a value)
        if (mmOperator && mmOperator !== 'range') {
          if (mmOperator === 'equals') {
            const opVal = params[mmOperatorValueKey]
            if (opVal !== undefined && opVal !== '') return true
          } else {
            return true
          }
        }

        // Handle custom columns differently - they don't have section prefixes
        if (column.section_abbreviation === 'custom') {
          // For custom columns, use underscore format: custom_column_min_{id}
          const customColumnId = column.accessor_key.slice('custom_column_'.length)
          const minKey = `custom_column_min_${customColumnId}`
          const maxKey = `custom_column_max_${customColumnId}`
          return !!(
            (params[minKey] && params[minKey] !== '') ||
            (params[maxKey] && params[maxKey] !== '')
          )
        } else {
          // Regular column handling
          const prefix = column.section_abbreviation?.toLowerCase()
          const suffix = column.accessor_key?.slice(prefix?.length || 0) || ''
          const minKey = `${prefix}Min${suffix}`
          const maxKey = `${prefix}Max${suffix}`
          return !!(
            (params[minKey] && params[minKey] !== '') ||
            (params[maxKey] && params[maxKey] !== '')
          )
        }
      }
      case 'date':
        // Handle custom columns differently - they use a different key format
        if (column.section_abbreviation === 'custom') {
          // For custom columns, extract the ID from accessor_key (format: custom_column_123)
          const customColumnId = column.accessor_key.slice('custom_column_'.length)
          const minKey = `custom_column_min_${customColumnId}`
          const maxKey = `custom_column_max_${customColumnId}`
          const optionKey = `${column.accessor_key}Option`
          return !!(
            (params[minKey] && params[minKey] !== '') ||
            (params[maxKey] && params[maxKey] !== '') ||
            (params[optionKey] && params[optionKey] !== 'any')
          )
        } else {
          // Regular column handling
          const prefix = column.section_abbreviation?.toLowerCase()
          const suffix = column.accessor_key?.slice(prefix?.length || 0) || ''
          const minKey = `${prefix}Min${suffix}`
          const maxKey = `${prefix}Max${suffix}`
          const optionKey = `${prefix}${suffix}Option`
          return !!(
            (params[minKey] && params[minKey] !== '') ||
            (params[maxKey] && params[maxKey] !== '') ||
            (params[optionKey] && params[optionKey] !== 'any')
          )
        }
      case 'select':
        return !!(search && search !== '')
      case 'multiselect':
        return Array.isArray(search) && search.length > 0
      case 'mcc_select':
      case 'group_multiselect':
        return Array.isArray(search) && search.length > 0
      case 'greater_than':
        const gtPrefix = column.section_abbreviation?.toLowerCase()
        let gtKey = column.accessor_key

        if (
          gtPrefix &&
          column.accessor_key &&
          column.accessor_key.startsWith(gtPrefix)
        ) {
          const gtSuffix = column.accessor_key.slice(gtPrefix.length)
          gtKey = `${gtPrefix}Min${gtSuffix}`
        }

        return !!(params[gtKey] && params[gtKey] !== '')
      case 'reference':
        // Check if there are any employees matching this column's database_column_name
        const allEmployees = params.employees.concat(params.excludedEmployees)
        
        const matchingEmployees = allEmployees?.filter(emp =>
          emp.database_column_name === column.database_column_name
        )
        if (matchingEmployees?.length > 0) {
          return true
        }
        return !!(search && search !== '')
      default:

        return false
    }
  }

  return (
    <th style={{ cursor: 'default', ...style }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px'
        }}
      >
        {title}
        {column && renderFilterPopover()}
      </span>
    </th>
  )
}

export default FilterableHeader
